import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

const pick = (items) => items[Math.floor(Math.random() * items.length)];

const SQUARE_FREE = [2, 3, 5, 6, 7, 10, 11, 13];
const CUBE_FREE = [2, 3, 5, 6, 7, 10, 11, 13];

function toRawAnswer(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value.raw ?? value.value ?? value.latex ?? value.text ?? "");
}

function normalizeExpression(value) {
  return toRawAnswer(value)
    .replace(/\s+/g, "")
    .replace(/[−–]/g, "-")
    .replace(/\\\\sqrt\[3\]/g, "∛")
    .replace(/\\sqrt\[3\]/g, "∛")
    .replace(/sqrt\[3\]/g, "∛")
    .replace(/\\\\sqrt/g, "√")
    .replace(/\\sqrt/g, "√")
    .replace(/sqrt/g, "√")
    .replace(/[∛√]\{([^}]+)\}/g, (match, inside) => `${match[0]}${inside}`)
    .replace(/[∛√]\(([^)]+)\)/g, (match, inside) => `${match[0]}${inside}`);
}

function evaluateExpression(value) {
  const normalized = normalizeExpression(value);
  if (!normalized) return NaN;
  try {
    const parsed = normalized
      .replace(/(\d*)∛(\d+)/g, (_, coefficient, radicand) =>
        `${coefficient || "1"}*Math.cbrt(${radicand})`
      )
      .replace(/(\d*)√(\d+)/g, (_, coefficient, radicand) =>
        `${coefficient || "1"}*Math.sqrt(${radicand})`
      );
    return Function(`return ${parsed}`)();
  } catch {
    return NaN;
  }
}

function primeFactorization(number) {
  const factors = [];
  let remaining = number;
  for (let prime = 2; prime * prime <= remaining; prime += 1) {
    let exponent = 0;
    while (remaining % prime === 0) {
      remaining /= prime;
      exponent += 1;
    }
    if (exponent) factors.push([prime, exponent]);
  }
  if (remaining > 1) factors.push([remaining, 1]);
  return factors;
}

function factorizationLatex(number) {
  return primeFactorization(number)
    .map(([prime, exponent]) => exponent === 1 ? `${prime}` : `${prime}^{${exponent}}`)
    .join("\\cdot ");
}

function factorizationLadderLatex(number) {
  const factors = primeFactorization(number)
    .flatMap(([prime, exponent]) => Array(exponent).fill(prime));
  let remaining = number;
  const rows = factors.map((prime) => {
    const row = `${remaining} & ${prime} \\\\`;
    remaining /= prime;
    return row;
  });
  rows.push("1 & \\\\");
  return `\\begin{array}{r|l}
${rows.join("\n")}
\\end{array}`;
}

function rootLatex(degree, radicand) {
  return degree === 3 ? `\\sqrt[3]{${radicand}}` : `\\sqrt{${radicand}}`;
}

function resultLatex(degree, coefficient, remainder) {
  return `${coefficient}${rootLatex(degree, remainder)}`;
}

export function generateTask(options = {}) {
  const degree = options.degree === 3 ? 3 : 2;
  const coefficient = pick(degree === 3 ? [2, 3, 4] : [2, 3, 4, 5, 6]);
  const remainder = pick(degree === 3 ? CUBE_FREE : SQUARE_FREE);
  const radicand = coefficient ** degree * remainder;
  const expressionLatex = rootLatex(degree, radicand);
  const correctLatex = resultLatex(degree, coefficient, remainder);

  return {
    type: "ExtractRootFactorTask",
    data: {
      degree,
      coefficient,
      remainder,
      radicand,
      expressionLatex,
      correctLatex,
      primeFactorLatex: factorizationLatex(radicand),
      primeFactorLadderLatex: factorizationLadderLatex(radicand),
    },
    tags: ["pierwiastki", "wyłączanie-czynnika", degree === 3 ? "cuberoot" : "sqrt"],
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        Wyłącz czynnik przed znak pierwiastka:{" "}
        <InlineMath math={data.expressionLatex} />.
      </p>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <SqrtInput
      value={toRawAnswer(userAnswer)}
      onChange={onChange}
      disabled={disabled}
      wide
      allowCubeRoot={data.degree === 3}
      onlyCubeRoot={data.degree === 3}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const expected = evaluateExpression(data.correctLatex);
  const provided = evaluateExpression(userAnswer);
  return Number.isFinite(provided) && Math.abs(expected - provided) < 0.01;
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.correctLatex} />;
}

export function renderUserAnswer(userAnswer) {
  const raw = toRawAnswer(userAnswer);
  return raw ? <InlineMath math={normalizeExpression(raw)} /> : <i>(brak odpowiedzi)</i>;
}

export function renderExplanation({ data }) {
  const groupName = data.degree === 3 ? "trójki" : "pary";
  const power = data.degree === 3 ? "sześcian" : "kwadrat";

  return (
    <div className="space-y-3">
      <p>
        <strong>Krok 1.</strong> Rozkładamy liczbę pod pierwiastkiem na
        czynniki pierwsze, dzieląc ją kolejno przez najmniejsze dzielniki.
      </p>
      <BlockMath math={data.primeFactorLadderLatex} />
      <BlockMath math={`${data.radicand}=${data.primeFactorLatex}`} />

      <p>
        <strong>Krok 2.</strong> Łączymy jednakowe czynniki w {groupName}.
        Każdy pełny {power} możemy wyłączyć przed znak pierwiastka.
      </p>
      <BlockMath
        math={`${data.expressionLatex}
          =${rootLatex(data.degree, `${data.coefficient}^{${data.degree}}\\cdot ${data.remainder}`)}
          =${data.correctLatex}`}
      />
    </div>
  );
}
